import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { AuthProvider, useAuth } from "@/lib/auth.jsx";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import DashboardRouter from "@/pages/DashboardRouter";
import DocShareLanding from "@/pages/DocShareLanding";
import PublicFormPage from "@/pages/public/PublicFormPage";
import PublicMatrixPage from "@/pages/public/PublicMatrixPage";
import "@/App.css";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || "";

// Global double-click guard: a second click on the SAME button within
// 700ms is swallowed in the capture phase, before React sees it. This
// stops "click twice → action fires twice" bugs app-wide (duplicate
// receipts, duplicate allotments, double emails) without having to add a
// busy-state to every single button. Legitimate rapid clicks on DIFFERENT
// elements are unaffected; checkbox/radio/label toggles are exempt.
function useDoubleClickGuard() {
  useEffect(() => {
    const last = new WeakMap();
    const handler = (e) => {
      const el = e.target.closest("button, [role='button']");
      if (!el || el.disabled) return;
      const now = Date.now();
      const prev = last.get(el);
      if (prev && now - prev < 700) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      last.set(el, now);
    };
    document.addEventListener("click", handler, true);
    return () => document.removeEventListener("click", handler, true);
  }, []);
}

function Guard({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-slate-400 text-sm">Loading…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AppRouter() {
  useDoubleClickGuard();
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/share/doc/:token" element={<DocShareLanding />} />
      <Route path="/f/:slug" element={<PublicFormPage />} />
      <Route path="/matrix/:token" element={<PublicMatrixPage />} />
      <Route path="/dashboard/*" element={<Guard><DashboardRouter /></Guard>} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
        <BrowserRouter>
          <AuthProvider>
            <AppRouter />
            <Toaster richColors position="top-right" />
          </AuthProvider>
        </BrowserRouter>
      </GoogleOAuthProvider>
    </div>
  );
}
